import { UzButton, UzDateRangePicker, type UzDateRange, type UzDateRangePreset } from '@utazzo/ui';
import { Download } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { toast } from 'sonner';

import { exportLpsLeads, type LpsLeadExportKind } from '../export-leads';
import { useLpsDestinationSummary } from '../use-lps';

import type { LpsLeadType } from '../lps.api';

import {
  isoDate,
  LEAD_RANGE_PRESETS,
  type LeadDateRangePresetKey,
} from '@/features/leads/lead-date-range';

/**
 * The assigned-date window, owned by the tab so the grid's "Assigned" picker
 * and this table always show the same days: change one and both move.
 */
export interface LpsAssignedWindow {
  preset: LeadDateRangePresetKey;
  range: UzDateRange;
  onPresetSelect: (p: UzDateRangePreset) => void;
  onValueChange: (r: UzDateRange) => void;
}

/** A zero reads as clutter in a grid this dense; an empty cell reads as "none". */
const cell = (n: number): React.ReactNode =>
  n === 0 ? <span className="text-zinc-300">-</span> : n;

const border = 'border border-zinc-200';
const head = `${border} bg-zinc-50 px-3 py-1.5 text-center font-medium whitespace-nowrap`;
const num = `${border} px-3 py-2 text-center tabular-nums`;

/**
 * Who is working which destination: agents down the side, destinations across
 * the top, and under each destination two counts:
 *
 * - Cumulative: leads the agent was handed in the period, whenever they came
 *   in. Yesterday's unassigned leads handed out today land here today.
 * - Received: leads that arrived in the period. The unassigned row only has
 *   this one, since nobody was handed those.
 *
 * Answers the morning question - "are the Kashmir leads reaching the Kashmir
 * people, and is anyone sitting idle" - without opening a single lead.
 */
export const LpsDestinationSummary = ({
  window,
  leadType,
  variant = 'lps',
}: {
  window: LpsAssignedWindow;
  /** The page-level All / Inorganic / Organic switch. */
  leadType: LpsLeadType | 'all';
  /**
   * 'marketing' reads the same data through the Marketing Report's own endpoint
   * (and section permission) and hides the LPS-only export buttons. Default
   * 'lps' is the native Lead Parsing System view.
   */
  variant?: 'lps' | 'marketing';
}): React.JSX.Element => {
  const from = isoDate(window.range.from);
  const to = isoDate(window.range.to);
  const query = useMemo(
    () => ({
      ...(from ? { from } : {}),
      ...(to ? { to } : {}),
      ...(leadType !== 'all' ? { leadType } : {}),
    }),
    [from, to, leadType],
  );
  const summary = useLpsDestinationSummary(
    query,
    variant === 'marketing' ? '/reports/marketing/leads-by-destination' : undefined,
  );

  const columns = summary.data?.destinations ?? [];
  const rows = summary.data?.agents ?? [];
  const keyOf = (destination: string | null): string => destination ?? '';
  const today = window.preset === 'today';
  const cumulativeLabel = today ? "Today's Cumulative" : 'Cumulative';
  const receivedLabel = today ? "Today's" : 'Received';

  // The two counts, as the leads behind them - one file each.
  const [exporting, setExporting] = useState<LpsLeadExportKind | null>(null);
  const exportLeads = async (kind: LpsLeadExportKind): Promise<void> => {
    setExporting(kind);
    try {
      const count = await exportLpsLeads({
        kind,
        ...(from ? { from } : {}),
        ...(to ? { to } : {}),
        ...(leadType !== 'all' ? { leadType } : {}),
      });
      if (count === 0) toast.info('No leads to export for this period.');
      else toast.success(`Exported ${count} lead${count === 1 ? '' : 's'}.`);
    } catch {
      toast.error('Could not export the leads. Try again.');
    } finally {
      setExporting(null);
    }
  };

  return (
    <section className="rounded-lg border border-zinc-200 bg-white p-4">
      <header className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold">Leads by destination and agent</h2>
          <p className="text-xs text-zinc-500">
            {summary.data
              ? `${summary.data.total} received, ${summary.data.assignedTotal} assigned, ${summary.data.inCrm} in the CRM, ${summary.data.waiting} waiting for an agent.`
              : 'Counts per agent for each destination.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {/* Export hits the LPS-only export endpoint, so it is hidden on the
              Marketing Report (which serves this under its own permission). */}
          {variant !== 'marketing' && (
            <>
              <UzButton
                size="sm"
                variant="outline"
                leftIcon={<Download className="size-3.5" />}
                loading={exporting === 'cumulative'}
                disabled={exporting !== null}
                tooltip="Leads handed to an agent in this period, whenever they arrived"
                onClick={() => void exportLeads('cumulative')}
              >
                Export {cumulativeLabel}
              </UzButton>
              <UzButton
                size="sm"
                variant="outline"
                leftIcon={<Download className="size-3.5" />}
                loading={exporting === 'received'}
                disabled={exporting !== null}
                tooltip="Leads that arrived in this period, whoever holds them now"
                onClick={() => void exportLeads('received')}
              >
                Export {today ? "Today's Leads" : 'Received'}
              </UzButton>
            </>
          )}
          {/* Same picker as the grid's "Assigned" filter, on the same state. */}
          <UzDateRangePicker
            value={window.range}
            presets={LEAD_RANGE_PRESETS}
            activePresetValue={window.preset === 'custom' ? null : window.preset}
            triggerClassName="h-[34px] text-[13px]"
            placeholder="Any time"
            onPresetSelect={window.onPresetSelect}
            onValueChange={window.onValueChange}
          />
        </div>
      </header>

      {summary.isError && (
        <p className="text-sm text-red-700">Could not load the destination breakdown.</p>
      )}
      {!summary.isError && rows.length === 0 && (
        <p className="text-sm text-zinc-500">
          {summary.isLoading
            ? 'Loading...'
            : !from
              ? 'No leads yet.'
              : `No leads ${from} to ${to ?? from}. Pick a longer period to see older leads.`}
        </p>
      )}

      {rows.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              {/* Two header rows: the destination spans its pair of counts. */}
              <tr className="text-xs font-semibold text-zinc-700">
                <th
                  rowSpan={2}
                  className={`sticky left-0 z-10 ${border} bg-zinc-50 px-3 py-2 text-left align-middle`}
                >
                  Agent
                </th>
                {columns.map((c) => (
                  <th key={keyOf(c.destination)} colSpan={2} className={head}>
                    {c.destination ?? 'No destination'}
                  </th>
                ))}
                <th colSpan={2} className={head}>
                  Total
                </th>
              </tr>
              <tr className="text-[11px] font-medium text-zinc-500">
                {columns.map((c) => (
                  <React.Fragment key={keyOf(c.destination)}>
                    <th className={head}>{cumulativeLabel}</th>
                    <th className={head}>{receivedLabel}</th>
                  </React.Fragment>
                ))}
                <th className={head}>{cumulativeLabel}</th>
                <th className={head}>{receivedLabel}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const unassigned = row.agentId === null;
                return (
                  <tr key={row.agentId ?? 'nobody'}>
                    <td
                      className={`sticky left-0 z-10 ${border} bg-white px-3 py-2 font-medium whitespace-nowrap`}
                    >
                      {row.agentName ?? <span className="text-amber-700">Unassigned</span>}
                    </td>
                    {columns.map((c) => (
                      <React.Fragment key={keyOf(c.destination)}>
                        {/* Nobody is "handed" an unassigned lead: leave the cell blank. */}
                        <td className={num}>
                          {unassigned ? '' : cell(row.assignedCounts[keyOf(c.destination)] ?? 0)}
                        </td>
                        <td className={num}>{cell(row.counts[keyOf(c.destination)] ?? 0)}</td>
                      </React.Fragment>
                    ))}
                    <td className={`${num} font-semibold`}>
                      {unassigned ? '' : row.assignedTotal}
                    </td>
                    <td className={`${num} font-semibold`}>{row.total}</td>
                  </tr>
                );
              })}
              <tr className="bg-emerald-50 font-semibold text-emerald-700">
                <td
                  className={`sticky left-0 z-10 ${border} bg-emerald-50 px-3 py-2 text-zinc-900`}
                >
                  Total
                </td>
                {columns.map((c) => (
                  <React.Fragment key={keyOf(c.destination)}>
                    <td className={num}>{c.assignedTotal}</td>
                    <td className={num}>{c.total}</td>
                  </React.Fragment>
                ))}
                <td className={num}>{summary.data?.assignedTotal ?? 0}</td>
                <td className={num}>{summary.data?.total ?? 0}</td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};
